import times from "lodash/times";
import React from "react";

import type { PolymorphicBoxProps } from "~/components/polymorphic-box";
import { isFunction } from "~/tools/is-function";
import { mapChildren } from "~/tools/map-children";
import type { OneOrMany } from "~/tools/one-or-many";
import { usePseudoRandomIntGenerator } from "~/tools/use-pseudo-random-int-generator";

import { SkeletonBoundary } from "./boundary";

const DEFAULT_ELEMENT = React.Fragment;

export type SkeletonListChildRenderer<TValue> = (
	value: undefined | TValue,
	index: number,
	self: ReadonlyArray<undefined | TValue>,
	percent: number,
) => React.ReactElement;

export interface SkeletonListProps<TValue> {
	/**
	 * A count of loading placeholders to render.
	 */
	count: number;

	/**
	 * An array of values to render.
	 */
	each: undefined | null | ReadonlyArray<TValue>;

	/**
	 * @default undefined
	 */
	empty?: React.ReactNode;

	/**
	 * Item renderer. When `each` is defined, the function will be called for each value.
	 * Otherwise, the function will be called against `times(count)` so the `value` is undefined.
	 * It assigns default `key` as `index`, but you can override it.
	 */
	children: OneOrMany<React.ReactNode | SkeletonListChildRenderer<TValue>>;
}

export const SkeletonList: <TValue, TElement extends React.ElementType = typeof DEFAULT_ELEMENT>(
	props: PolymorphicBoxProps<TElement, SkeletonListProps<TValue>>,
) => React.ReactNode = React.forwardRef(
	(
		{ el, count, each, empty, children, ...props }: PolymorphicBoxProps<React.ElementType, SkeletonListProps<unknown>>,
		ref,
	) => {
		const generatePseudoRandomPercent = usePseudoRandomIntGenerator({
			min: 0,
			max: 99,
			step: 1,
		});
		const fallbackCount = each == null ? count : 0;
		const fallback = React.useMemo(() => (fallbackCount ? times(fallbackCount, () => undefined) : []), [fallbackCount]);
		const list = each ?? fallback;

		return (
			<SkeletonBoundary el={el ?? DEFAULT_ELEMENT} ref={ref} when={each} {...props}>
				{mapChildren(children, (child, index) => {
					if (!isFunction(child)) {
						const key = React.isValidElement(child) ? child.key : null;

						return <React.Fragment key={key ?? index}>{child}</React.Fragment>;
					}

					if (list.length === 0) {
						return <React.Fragment key={index}>{empty}</React.Fragment>;
					}

					return (
						<React.Fragment key={index}>
							{list.map((value, valueIndex) => {
								const rendered = child(value, valueIndex, list, generatePseudoRandomPercent(valueIndex));

								return <React.Fragment key={rendered.key ?? valueIndex}>{rendered}</React.Fragment>;
							})}
						</React.Fragment>
					);
				})}
			</SkeletonBoundary>
		);
	},
);
